import { compareDisplayNames } from '@utils/string.util';
import { SelectionModel } from '@angular/cdk/collections';
import { CommonModule } from '@angular/common';
import {
  AfterViewChecked,
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Inject,
  OnChanges,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import {
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogActions,
  MatDialogClose,
  MatDialogContent,
  MatDialogRef,
  MatDialogTitle,
} from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIcon, MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatListModule } from '@angular/material/list';
import { MatRadioModule } from '@angular/material/radio';
import { MatSelectModule } from '@angular/material/select';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatSort, MatSortModule } from '@angular/material/sort';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import {
  AlertDialogComponent,
  DialogOption as AlertDialogOption,
} from '@components/shared/alert-dialog/alert-dialog.component';
import { firstValueFrom } from 'rxjs';
import { differenceInDays } from 'date-fns';

export interface PeriodicElement {
  name: string;
  position: number;
}

/** ダイアログのViewModel */
export type DeviceSettingGroupDialogOption = {
  updating: boolean;
  deviceTypeList: DeviceTypeOption[];
  target?: DeviceSettingGroupEditViewModel;
};

export type DeviceTypeOption = {
  deviceTypeKey: string;
  deviceTypeId: string;
  displayName: string;
};

export type DeviceContractViewModel = {
  /** シリアル番号 */
  deviceId: string;
  /** テナントID */
  tenantId: string;
  /** LPN ID */
  lpnId: string;
  /** 契約種別 */
  contractType: string;
  /** 契約開始日 */
  contractStartedAt: string;
  /** 契約終了日 */
  contractExpiredAt: string;
};

export type DeviceViewModel = {
  /** シリアル番号 */
  deviceId: string;
  /** デバイス名 */
  deviceName: string;
  /** デバイス種類ID */
  deviceTypeId: string;
  /** デバイス種類内部キー */
  deviceTypeKey?: string;
  /** デバイス種類表示名 */
  deviceTypeDisplayName?: string;
  /** テナントID */
  tenantId: string;
  /** デバイス契約情報 */
  deviceContract?: DeviceContractViewModel;
  /** 期間終了フラグ */
  expired?: boolean;
};

/** デバイス設定のViewModel */
export type DeviceSettingGroupEditViewModel = {
  /** 設定グループID */
  deviceSettingGroupId?: string;
  /** 設定グループ名 */
  deviceSettingGroupName?: string;
  /** デバイス種類 */
  deviceTypeId?: string;
  /** デバイス種類内部キー */
  deviceTypeKey?: string;
  /** グループ説明 */
  description?: string;
  /** グループに所属するデバイス */
  groupDeviceList?: DeviceViewModel[];
  /** デバイス一覧 */
  deviceList?: DeviceViewModel[];
};

/** フォームグループ */
type DeviceSettingGroupFormGroup = FormGroup<{
  /** 設定グループ名 */
  deviceSettingGroupName: FormControl<string>;
  /** デバイス種類 */
  deviceTypeKey: FormControl<string>;
  /** グループ説明 */
  description: FormControl<string>;
}>;

@Component({
  selector: 'app-device-group-edit-dialog',
  standalone: true,
  imports: [
    FormsModule,
    MatDialogTitle,
    MatDialogContent,
    MatDialogActions,
    MatDialogClose,
    ReactiveFormsModule,
    MatFormFieldModule,
    CommonModule,
    MatInputModule,
    MatSelectModule,
    MatSlideToggleModule,
    MatRadioModule,
    MatIcon,
    MatTooltipModule,
    MatIconModule,
    MatButtonModule,
    MatListModule,
    MatTableModule,
    MatSortModule,
    MatCheckboxModule,
  ],
  templateUrl: './device-group-edit-dialog.component.html',
  styleUrl: './device-group-edit-dialog.component.scss',
})
export class DeviceSettingGroupEditDialogComponent
  implements AfterViewInit, AfterViewChecked, OnChanges
{
  public dataSourceGroupDevice = new MatTableDataSource<DeviceViewModel>([]);

  public dataSourceDevice = new MatTableDataSource<DeviceViewModel>([]);

  public displayedColumns: string[] = ['checkbox', 'deviceName', 'deviceId'];

  /** ソート要素 */
  @ViewChild(MatSort) sort!: MatSort;

  /** 更新中 */
  public updating: boolean;

  /** フォームグループ */
  public formGroup: DeviceSettingGroupFormGroup;

  /** デバイス設定グループID */
  public deviceSettingGroupId: string = '';

  /** デバイス種類ID一覧 */
  public deviceTypeList: DeviceTypeOption[] = [];

  /** デバイス種類ID */
  public deviceTypeId: string = '';

  /** 無所属のデバイス一覧 */
  public deviceList: DeviceViewModel[] = [];

  /** グループに所属するデバイス一覧 */
  public groupDeviceList: DeviceViewModel[] = [];

  /** 選択した無所属のデバイス */
  public selectedDevice: DeviceViewModel[] = [];

  /** 選択したグループに所属するデバイス */
  public selectedGroupDevice: DeviceViewModel[] = [];

  /** 無所属のデバイス名キーワード */
  public deviceNameKeyword = '';

  /** 無所属のデバイスIDキーワード */
  public deviceIdKeyword = '';

  /** グループに所属するデバイス名キーワード */
  public groupDeviceNameKeyword = '';

  /** グループに所属するデバイスIDキーワード */
  public groupDeviceIdKeyword = '';

  /** 選択状態 */
  public selectionGroupDevice = new SelectionModel<DeviceViewModel>(true, []);

  /** 選択状態 */
  public selectionDevice = new SelectionModel<DeviceViewModel>(true, []);

  /** デバイス名入力フォーム*/
  @ViewChild('deviceNameInput') deviceNameInputElement!: ElementRef;

  constructor(
    @Inject(MAT_DIALOG_DATA) options: DeviceSettingGroupDialogOption,
    private dialogRef: MatDialogRef<DeviceSettingGroupFormGroup>,
    private dialog: MatDialog,
    private readonly changeDetectorRef: ChangeDetectorRef,
  ) {
    const deviceTypeKey = options.target?.deviceTypeKey || '';
    this.deviceSettingGroupId = options.target?.deviceSettingGroupId || '';
    let groupDeviceList = options.target?.groupDeviceList || [];
    for (const device of groupDeviceList) {
      if (device.deviceContract) {
        const result = differenceInDays(
          new Date(),
          new Date(device.deviceContract.contractExpiredAt),
        );
        device.expired = result > 0;
      }
    }
    groupDeviceList = groupDeviceList.sort((first, second) =>
      first.deviceName > second.deviceName ? 1 : -1,
    );
    this.dataSourceGroupDevice.data = groupDeviceList;
    this.dataSourceGroupDevice.data = this.dataSourceGroupDevice.data.filter(
      (device) => device.deviceTypeKey === deviceTypeKey,
    );
    let deviceList = options.target?.deviceList || [];
    for (const device of deviceList) {
      if (device.deviceContract) {
        const result = differenceInDays(
          new Date(),
          new Date(device.deviceContract.contractExpiredAt),
        );
        device.expired = result > 0;
      }
    }
    deviceList = deviceList.sort((first, second) =>
      first.deviceName > second.deviceName ? 1 : -1,
    );
    this.dataSourceDevice.data = deviceList;
    this.dataSourceDevice.data = this.dataSourceDevice.data.filter(
      (device) => device.deviceTypeKey === deviceTypeKey,
    );
    this.updating = options.updating;
    this.deviceTypeList = options.deviceTypeList.slice().sort((a, b) =>
      compareDisplayNames(a.displayName, b.displayName),
    );
    this.groupDeviceList = options.target?.groupDeviceList || [];
    this.deviceList = options.target?.deviceList || [];

    this.formGroup = new FormGroup({
      deviceSettingGroupName: new FormControl(
        options.target?.deviceSettingGroupName || '',
        {
          nonNullable: true,
          validators: [Validators.maxLength(50)],
        },
      ),
      deviceTypeKey: new FormControl(options.target?.deviceTypeKey || '', {
        nonNullable: true,
        validators: [Validators.required],
      }),
      description: new FormControl(options.target?.description || '', {
        nonNullable: true,
        validators: [Validators.maxLength(200)],
      }),
    });
  }

  ngAfterViewChecked(): void {
    this.changeDetectorRef.detectChanges();
  }

  ngAfterViewInit(): void {
    // 無所属のデバイステーブルのフィルタリング条件
    this.dataSourceDevice.filterPredicate = (data: DeviceViewModel) => {
      return (
        data.deviceName
          .toLowerCase()
          .includes(this.deviceNameKeyword.toLowerCase()) &&
        data.deviceId.toLowerCase().includes(this.deviceIdKeyword.toLowerCase())
      );
    };

    // 所属しているデバイステーブルのフィルタリング条件
    this.dataSourceGroupDevice.filterPredicate = (data: DeviceViewModel) => {
      return (
        data.deviceName
          .toLowerCase()
          .includes(this.groupDeviceNameKeyword.toLowerCase()) &&
        data.deviceId
          .toLowerCase()
          .includes(this.groupDeviceIdKeyword.toLowerCase())
      );
    };
  }

  public ngOnChanges(changes: SimpleChanges): void {
    this.dataSourceDevice.data = changes['deviceList'].currentValue;
  }

  /**
   * 送信
   */
  public async submit() {
    const deviceSettingGroupName = this.formGroup.get(
      'deviceSettingGroupName',
    )?.value;
    const operation = this.updating ? '変更' : '新規登録';
    const options: AlertDialogOption = {
      type: 'warn',
      title: 'Confirm',
      message: `設定グループ名「${deviceSettingGroupName}」を${operation}してよろしいですか？`,
      operation: operation,
    };

    const result: boolean = await firstValueFrom(
      this.dialog
        .open(AlertDialogComponent, {
          width: '600px',
          data: options,
        })
        .afterClosed(),
    );

    if (!result) {
      return;
    }

    const deviceTypeKey = this.formGroup.get('deviceTypeKey')?.value || '';
    const selectedDeviceType = this.deviceTypeList.find(
      (deviceType) => deviceType.deviceTypeKey === deviceTypeKey,
    );
    if (!selectedDeviceType) {
      this.formGroup.controls.deviceTypeKey.setErrors({ invalidDeviceType: true });
      return;
    }
    const groupDeviceList = this.groupDeviceList.filter(
      (device) => device.deviceTypeKey === deviceTypeKey,
    );
    const deviceSetting: DeviceSettingGroupEditViewModel = {
      deviceSettingGroupId: this.deviceSettingGroupId,
      deviceSettingGroupName:
        this.formGroup.get('deviceSettingGroupName')?.value || '',
      deviceTypeId: selectedDeviceType.deviceTypeId,
      deviceTypeKey,
      description: this.formGroup.get('description')?.value || '',
      groupDeviceList: groupDeviceList,
    };
    this.dialogRef.close(deviceSetting);
  }

  /**
   * すべての行を選択／非選択に切り替える
   */
  public toggleAllGroupDeviceRows() {
    if (this.isAllGroupDeviceSelected()) {
      this.selectionGroupDevice.clear();
      return;
    }

    this.selectionGroupDevice.select(
      ...this.dataSourceGroupDevice.filteredData,
    );
  }

  /**
   * すべての行を選択／非選択に切り替える
   */
  public toggleAllDeviceRows() {
    if (this.isAllDeviceSelected()) {
      this.selectionDevice.clear();
      return;
    }

    this.selectionDevice.select(...this.dataSourceDevice.filteredData);
  }

  /**
   * すべて選択されているか
   *
   * @returns すべて選択されていればtrue, 非選択があればfalse
   */
  public isAllGroupDeviceSelected() {
    const numSelected = this.selectionGroupDevice.selected.length;
    const numRows = this.dataSourceGroupDevice.filteredData.length;
    return numSelected === numRows;
  }

  /**
   * すべて選択されているか
   *
   * @returns すべて選択されていればtrue, 非選択があればfalse
   */
  public isAllDeviceSelected() {
    const numSelected = this.selectionDevice.selected.length;
    const numRows = this.dataSourceDevice.filteredData.length;
    return numSelected === numRows;
  }

  /**
   * 選択した設定情報エリアのタグを選択済みとしてマークする。
   * @param item 選択したタグ
   */
  public toggleGroupDeviceSelection(item: DeviceViewModel) {
    if (this.groupDeviceList.includes(item)) {
      this.groupDeviceList = this.groupDeviceList.filter((i) => i !== item);
    } else {
      this.groupDeviceList.push(item);
    }
  }

  /**
   * 選択したタグ一覧エリアのタグを選択済みとしてマークする。
   * @param item 選択したタグ
   */
  public toggleDeviceSelection(item: DeviceViewModel) {
    if (this.deviceList.includes(item)) {
      this.deviceList = this.deviceList.filter((i) => i !== item);
    } else {
      this.deviceList.push(item);
    }
  }

  /**
   * 選択したデバイスをグループに所属するデバイス一覧エリアに移動する。
   * @param tagList 選択したデバイス一覧
   */
  public moveToGroupDeviceList() {
    const deviceTypeKey = this.formGroup.get('deviceTypeKey')?.value || '';
    for (const device of this.selectionDevice.selected) {
      this.groupDeviceList.push(device);
      this.dataSourceGroupDevice.data = this.groupDeviceList.filter(
        (device) => device.deviceTypeKey === deviceTypeKey,
      );
      this.deviceList = this.deviceList.filter((i) => i !== device);
      this.dataSourceDevice.data = this.deviceList.filter(
        (device) => device.deviceTypeKey === deviceTypeKey,
      );
    }
    this.selectedDevice = [];
    this.selectedGroupDevice = [];
    this.selectionDevice.clear();
  }

  /**
   * 選択したデバイスを無所属のデバイス一覧エリアに移動する。
   * @param deviceList 選択したデバイス一覧
   */
  public moveToDeviceList() {
    const deviceTypeKey = this.formGroup.get('deviceTypeKey')?.value || '';
    for (const device of this.selectionGroupDevice.selected) {
      this.deviceList.push(device);
      this.dataSourceDevice.data = this.deviceList.filter(
        (device) => device.deviceTypeKey === deviceTypeKey,
      );
      this.groupDeviceList = this.groupDeviceList.filter((i) => i !== device);
      this.dataSourceGroupDevice.data = this.groupDeviceList.filter(
        (device) => device.deviceTypeKey === deviceTypeKey,
      );
    }
    this.selectedDevice = [];
    this.selectedGroupDevice = [];
    this.selectionGroupDevice.clear();
  }

  public filterDeviceType() {
    const deviceTypeKey = this.formGroup.get('deviceTypeKey')?.value || '';
    this.dataSourceDevice.data = this.deviceList.filter(
      (device) => device.deviceTypeKey === deviceTypeKey,
    );
    this.dataSourceGroupDevice.data = this.groupDeviceList.filter(
      (device) => device.deviceTypeKey === deviceTypeKey,
    );
  }

  /**
   * 無所属のデバイス一覧に対してテーブルヘッダーの検索欄の単語でフィルタリングする。
   */
  public applyDeviceDataSourceFilter(): void {
    // フィルタ処理を発火
    // ※空文字指定だとフィルタリングがされないためdummy文字列を指定している
    // （Angular materialのtable-data-source.tsの_filterDataメソッド参照）
    this.dataSourceDevice.filter = 'dummy';
  }

  /**
   * グループに所属するデバイス一覧に対してテーブルヘッダーの検索欄の単語でフィルタリングする。
   */
  public applyGroupDeviceDataSourceFilter(): void {
    // フィルタ処理を発火
    // ※空文字指定だとフィルタリングがされないためdummy文字列を指定している
    // （Angular materialのtable-data-source.tsの_filterDataメソッド参照）
    this.dataSourceGroupDevice.filter = 'dummy';
  }

  /**
   * 無所属のデバイス一覧に対するデバイス名でのフィルタリングをクリアする。
   */
  public clearDeviceNameFilter(): void {
    // 検索欄が空欄の場合はクリア処理をしない
    if (!this.deviceNameKeyword) {
      return;
    }

    // フィルタ条件をクリア
    this.deviceNameKeyword = '';

    // フィルタ処理を発火
    this.applyDeviceDataSourceFilter();
  }

  /**
   * 無所属のデバイス一覧に対するデバイスIDでのフィルタリングをクリアする。
   */
  public clearDeviceIdFilter(): void {
    // 検索欄が空欄の場合はクリア処理をしない
    if (!this.deviceIdKeyword) {
      return;
    }

    // フィルタ条件をクリア
    this.deviceIdKeyword = '';

    // フィルタ処理を発火
    this.applyDeviceDataSourceFilter();
  }

  /**
   * グループに所属するデバイス一覧に対するデバイス名でのフィルタリングをクリアする。
   */
  public clearGroupDeviceNameFilter(): void {
    // 検索欄が空欄の場合はクリア処理をしない
    if (!this.groupDeviceNameKeyword) {
      return;
    }

    // フィルタ条件をクリア
    this.groupDeviceNameKeyword = '';

    // フィルタ処理を発火
    this.applyGroupDeviceDataSourceFilter();
  }

  /**
   * グループに所属するデバイス一覧に対するデバイスIDでのフィルタリングをクリアする。
   */
  public clearGroupDeviceIdFilter(): void {
    // 検索欄が空欄の場合はクリア処理をしない
    if (!this.groupDeviceIdKeyword) {
      return;
    }

    // フィルタ条件をクリア
    this.groupDeviceIdKeyword = '';

    // フィルタ処理を発火
    this.applyGroupDeviceDataSourceFilter();
  }
}
